import React, { useState, useEffect, useRef } from 'react';
import {
  ThemeProvider, createTheme, CssBaseline, Box, AppBar, Toolbar, Typography, Button,
  Paper, Fab, Dialog, DialogTitle, DialogContent, DialogActions, Drawer, List,
  ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, IconButton, Chip,
  CircularProgress, useMediaQuery, BottomNavigation, BottomNavigationAction
} from '@mui/material';
import {
  Inventory as InventoryIcon, Assessment as ReportsIcon, ShoppingCart as SellIcon,
  Add as AddIcon, Store as StoreIcon, CalendarMonth as CalendarIcon,
  Menu as MenuIcon, Logout as LogoutIcon, ChevronRight as ChevronRightIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import InventorySpreadsheet from './components/InventorySpreadsheet';
import ReportsDashboard from './components/ReportsDashboard';
import QuickSell from './components/QuickSell';
import HomeDashboard from './components/HomeDashboard';
import SalesCalendar from './components/SalesCalendar';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import PublicHomepage from './components/PublicHomepage';
import CustomerCatalogPage from './components/CustomerCatalogPage';
import Login from './components/Login';
import BrandLogo from './components/BrandLogo';
import { getAuthSession, loginWithPassword, logoutSession } from './services/authClient';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#E13B36', light: '#F05A54', dark: '#B82723', contrastText: '#ffffff' },
    secondary: { main: '#E13B36', light: '#F05A54', dark: '#B82723', contrastText: '#ffffff' },
    background: { default: '#0B0D0F', paper: '#15181C' },
    text: { primary: '#F4F6F8', secondary: '#9AA1A9' },
    divider: '#282D33',
    action: { hover: 'rgba(255,255,255,0.045)', selected: 'rgba(225,59,54,0.14)' },
    success: { main: '#36A269' },
    warning: { main: '#D9A441' },
    error: { main: '#F05A54' },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: { borderRadius: 4 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: '#0B0D0F' },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 6, padding: '9px 16px', boxShadow: 'none' },
        contained: {
          backgroundColor: '#E13B36',
          '&:hover': { backgroundColor: '#F05A54', boxShadow: 'none' },
        },
        outlined: {
          borderColor: '#3A4047',
          color: '#F4F6F8',
          '&:hover': { borderColor: '#E13B36', backgroundColor: 'rgba(225,59,54,0.06)' },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none', backgroundColor: '#15181C', border: '1px solid #282D33' },
        elevation1: { boxShadow: '0 1px 2px rgba(0,0,0,0.22)' },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { backgroundImage: 'none', backgroundColor: '#15181C', border: '1px solid #282D33', boxShadow: 'none' },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 8, boxShadow: '0 18px 55px rgba(0,0,0,0.5)' },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          borderBottom: '2px solid rgba(255,255,255,0.12)',
          color: '#F5F5F5',
        },
        root: {
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          color: '#F5F5F5',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#1B1F24 !important',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'rgba(255,255,255,0.15)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255,255,255,0.3)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#E53935',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#9E9E9E',
            '&.Mui-focused': {
              color: '#E53935',
            },
          },
          '& .MuiInputAdornment-root .MuiSvgIcon-root': {
            color: '#9E9E9E',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          '& fieldset': {
            borderColor: 'rgba(255,255,255,0.15)',
          },
          '&:hover fieldset': {
            borderColor: 'rgba(255,255,255,0.3)',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#E53935',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#9E9E9E',
          '&.Mui-focused': {
            color: '#E53935',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.15)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.3)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#E53935',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#15181C',
          border: '1px solid #282D33',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#F5F5F5',
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.06)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(229,57,53,0.15)',
            color: '#FF5252',
            '&:hover': {
              backgroundColor: 'rgba(229,57,53,0.25)',
            },
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(229,57,53,0.12)',
          color: '#FF5252',
          border: '1px solid rgba(229,57,53,0.25)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 4,
        },
      },
    },
  },
});

const SHOPS = ['Az Tires', 'LJ Tires', 'AZTires - MCP', 'LJTires - MCP'];
const SHOP_STORAGE_KEY = 'aztires_selected_shop';
const DRAWER_WIDTH = 260;
const AUTH_IDLE_TIMEOUT_MS = 15 * 60 * 1000;

function AdminApp({ onLogout, user }) {
  const [currentShop, setCurrentShop] = useState(() => localStorage.getItem(SHOP_STORAGE_KEY) || 'Az Tires');
  const [currentView, setCurrentView] = useState('home');
  const [shopDialogOpen, setShopDialogOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const inventoryRef = useRef(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    localStorage.setItem(SHOP_STORAGE_KEY, currentShop);
  }, [currentShop]);

  const handleShopChange = (shop) => {
    if (shop !== currentShop) { setCurrentShop(shop); }
    setShopDialogOpen(false);
  };

  const renderView = () => {
    switch (currentView) {
      case 'home': return <HomeDashboard shopName={currentShop} />;
      case 'inventory': return <InventorySpreadsheet ref={inventoryRef} shopName={currentShop} />;
      case 'calendar': return <SalesCalendar shopName={currentShop} />;
      case 'reports': return <ReportsDashboard shopName={currentShop} />;
      case 'sell': return <QuickSell shopName={currentShop} />;
      default: return <HomeDashboard shopName={currentShop} />;
    }
  };

  const menuItems = [
    { id: 'home', label: 'Dashboard', icon: <SpeedIcon /> },
    { id: 'inventory', label: 'Inventory', icon: <InventoryIcon /> },
    { id: 'sell', label: 'Quick Sell', icon: <SellIcon /> },
    { id: 'calendar', label: 'Sales Calendar', icon: <CalendarIcon /> },
    { id: 'reports', label: 'Reports', icon: <ReportsIcon /> },
  ];
  const activeMenuItem = menuItems.find((item) => item.id === currentView) || menuItems[0];

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#0B0D0F' }}>
      <Box sx={{ px: 2.5, py: 2.25, borderBottom: '1px solid #20242A' }}>
        <BrandLogo height={44} />
      </Box>

      <Box sx={{ px: 2, pb: 2 }}>
        <Paper
          elevation={0}
          onClick={() => setShopDialogOpen(true)}
          sx={{
            p: 1.5, borderRadius: 1.5, border: '1px solid #282D33', bgcolor: '#111418', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 1.5,
            transition: 'border-color 0.18s, background-color 0.18s', '&:hover': { borderColor: '#3A4047', bgcolor: '#161A1F' }
          }}
        >
          <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: 'rgba(229,57,53,0.2)', color: '#FF5252', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <StoreIcon fontSize="small" />
          </Box>
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <Typography variant="caption" sx={{ color: '#BDBDBD', fontWeight: 600, display: 'block' }}>Current Store</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#ffffff', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{currentShop}</Typography>
          </Box>
          <ChevronRightIcon sx={{ color: '#BDBDBD', fontSize: 20 }} />
        </Paper>
      </Box>

      <Box sx={{ px: 2, pb: 2 }}>
        <Typography variant="caption" sx={{ color: '#BDBDBD', fontWeight: 700, display: 'block', mb: 0.5 }}>
          Signed in as
        </Typography>
        <Typography variant="body2" sx={{ color: '#F5F5F5', fontWeight: 600, wordBreak: 'break-word' }}>
          {user?.username || 'Admin'}
        </Typography>
      </Box>

      <Divider sx={{ mx: 2, mb: 2, borderColor: 'rgba(255,255,255,0.08)' }} />

      <List sx={{ px: 1.5, flex: 1 }}>
        {menuItems.map((item) => {
          const isSelected = currentView === item.id;
          return (
            <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={isSelected}
                onClick={() => { setCurrentView(item.id); setMobileOpen(false); }}
                sx={{
                  borderRadius: 1, py: 1.2, borderLeft: '3px solid transparent',
                  '&.Mui-selected': { bgcolor: 'rgba(225,59,54,0.12)', color: '#ffffff', borderLeftColor: '#E13B36', '&:hover': { bgcolor: 'rgba(225,59,54,0.16)' } },
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: isSelected ? '#ffffff' : '#BDBDBD' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.label} 
                  primaryTypographyProps={{ fontWeight: isSelected ? 700 : 500, fontSize: '0.9rem', color: isSelected ? '#ffffff' : '#BDBDBD' }} 
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          color="inherit"
          startIcon={<LogoutIcon />}
          onClick={onLogout}
          sx={{ borderColor: 'rgba(255,255,255,0.2)', color: '#BDBDBD', '&:hover': { borderColor: '#ffffff', bgcolor: 'rgba(255,255,255,0.06)', color: '#ffffff' } }}
        >
          Sign Out
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#0F1114' }}>
        {/* Mobile App Bar */}
        <AppBar position="fixed" elevation={0} sx={{ display: { md: 'none' }, bgcolor: '#0B0D0F', borderBottom: '1px solid #282D33', zIndex: theme.zIndex.drawer + 1 }}>
          <Toolbar sx={{ justifyContent: 'space-between', minHeight: 68, gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ color: '#F5F5F5', ml: 0, minHeight: 44, minWidth: 44 }}>
                <MenuIcon />
              </IconButton>
              <BrandLogo compact height={34} />
              <Typography variant="body2" sx={{ fontWeight: 800, color: '#F5F5F5' }}>{activeMenuItem.label}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                size="small"
                icon={<StoreIcon sx={{ fontSize: '1rem', color: '#E53935 !important' }} />}
                label={currentShop}
                onClick={() => setShopDialogOpen(true)}
                sx={{
                  fontWeight: 600, bgcolor: 'rgba(229,57,53,0.15)', color: '#E53935', borderRadius: 2,
                  maxWidth: 140, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' }
                }}
              />
              <IconButton onClick={onLogout} sx={{ color: '#BDBDBD', minHeight: 44, minWidth: 44 }}>
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Sidebar */}
        <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: '88vw', maxWidth: DRAWER_WIDTH, borderRight: 'none', boxShadow: '4px 0 24px rgba(0,0,0,0.3)' } }}
          >
            {drawerContent}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{ display: { xs: 'none', md: 'block' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH, borderRight: '1px solid #20242A', bgcolor: '#0B0D0F' } }}
            open
          >
            {drawerContent}
          </Drawer>
        </Box>

        {/* Main Content */}
        <Box component="main" sx={{ flexGrow: 1, p: { xs: 1.5, sm: 3, md: 4 }, pt: { xs: 10, md: 4 }, pb: { xs: 11, md: 4 }, width: { md: `calc(100% - ${DRAWER_WIDTH}px)` } }}>
          <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
            <Box sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 4, display: { xs: 'none', md: 'flex' } }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#F5F5F5', letterSpacing: '-1px' }}>
                {activeMenuItem.label}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ color: '#BDBDBD', fontWeight: 500 }}>
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </Typography>
              </Box>
            </Box>

            <Paper
              elevation={0}
              sx={{
                display: { xs: 'block', md: 'none' },
                mb: 2,
                p: 2,
                borderRadius: 1.5,
                border: '1px solid #282D33',
                background: '#15181C'
              }}
            >
              <Typography variant="caption" sx={{ color: '#E53935', fontWeight: 800, letterSpacing: '0.08em' }}>
                ACTIVE VIEW
              </Typography>
              <Typography sx={{ fontWeight: 800, color: '#F5F5F5', fontSize: '1.1rem', mt: 0.5 }}>
                {activeMenuItem.label}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
                <Chip label={currentShop} size="small" onClick={() => setShopDialogOpen(true)} sx={{ bgcolor: 'rgba(229,57,53,0.15)', color: '#E53935', fontWeight: 700 }} />
                <Chip label={user?.username || 'Admin'} size="small" sx={{ bgcolor: '#3A3A3A', color: '#F5F5F5', fontWeight: 700 }} />
              </Box>
            </Paper>
            
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              {renderView()}
            </Box>
          </Box>
        </Box>

        {/* Floating Action Button */}
        {currentView === 'inventory' && (
          <Fab
            color="secondary"
            onClick={() => inventoryRef.current?.addNewItem?.()}
            sx={{ position: 'fixed', bottom: { xs: 86, md: 24 }, right: { xs: 16, md: 24 }, boxShadow: '0 8px 24px rgba(229, 57, 53, 0.3)' }}
          >
            <AddIcon />
          </Fab>
        )}

        {isMobile && (
          <Paper
            elevation={8}
            sx={{
              position: 'fixed',
              left: 12,
              right: 12,
              bottom: 10,
              zIndex: theme.zIndex.appBar,
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.08)'
            }}
          >
            <BottomNavigation
              showLabels
              value={currentView}
              onChange={(event, nextView) => setCurrentView(nextView)}
              sx={{
                height: 68,
                '& .MuiBottomNavigationAction-root': { minWidth: 0, px: 0.5, minHeight: 56 },
                '& .MuiBottomNavigationAction-label': { fontSize: '0.7rem', fontWeight: 700, '&.Mui-selected': { fontSize: '0.7rem' } },
                '& .Mui-selected': { color: '#E53935' }
              }}
            >
              {menuItems.map((item) => (
                <BottomNavigationAction key={item.id} value={item.id} label={item.label.replace('Sales ', '')} icon={item.icon} />
              ))}
            </BottomNavigation>
          </Paper>
        )}

        {/* Shop Selection Dialog */}
        <Dialog open={shopDialogOpen} onClose={() => setShopDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Switch Store</Typography>
            <Typography variant="body2" color="text.secondary">Select the store location to manage.</Typography>
          </DialogTitle>
          <DialogContent>
            <List sx={{ pt: 0 }}>
              {SHOPS.map((shop) => (
                <ListItem key={shop} disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    selected={shop === currentShop}
                    onClick={() => handleShopChange(shop)}
                    sx={{
                      borderRadius: 2, border: '1px solid', borderColor: shop === currentShop ? '#E53935' : 'rgba(255,255,255,0.08)',
                      bgcolor: shop === currentShop ? 'rgba(229,57,53,0.15)' : '#2D2D2D', '&:hover': { bgcolor: shop === currentShop ? 'rgba(229,57,53,0.15)' : '#3A3A3A' }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: shop === currentShop ? '#E53935' : '#BDBDBD' }}>
                      <StoreIcon />
                    </ListItemIcon>
                    <ListItemText primary={shop} primaryTypographyProps={{ fontWeight: shop === currentShop ? 700 : 500, color: shop === currentShop ? '#E53935' : '#BDBDBD' }} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setShopDialogOpen(false)} color="inherit">Cancel</Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
}

const ProtectedAdmin = ({ authStatus, user, onLogin, onLogout }) => {
  if (authStatus === 'loading') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#1A1A1A' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (authStatus !== 'authenticated') {
    return <Login onLogin={onLogin} />;
  }

  return <AdminApp onLogout={onLogout} user={user} />;
};

export default function App() {
  const [authStatus, setAuthStatus] = useState('loading');
  const [currentUser, setCurrentUser] = useState(null);
  const idleTimerRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const loadSession = async () => {
      try {
        const session = await getAuthSession();

        if (!isMounted) {
          return;
        }

        if (session.authenticated) {
          setCurrentUser(session.user || null);
          setAuthStatus('authenticated');
          return;
        }

        setCurrentUser(null);
        setAuthStatus('unauthenticated');
      } catch (error) {
        console.error('Unable to restore session:', error);

        if (!isMounted) {
          return;
        }

        setCurrentUser(null);
        setAuthStatus('unauthenticated');
      }
    };

    loadSession();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (authStatus !== 'authenticated') {
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
      }
      return undefined;
    }

    const resetIdleTimer = () => {
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
      }

      idleTimerRef.current = window.setTimeout(async () => {
        try {
          await logoutSession();
        } catch (error) {
          console.error('Unable to end expired session cleanly:', error);
        } finally {
          setCurrentUser(null);
          setAuthStatus('unauthenticated');
        }
      }, AUTH_IDLE_TIMEOUT_MS);
    };

    const events = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'];
    events.forEach((eventName) => window.addEventListener(eventName, resetIdleTimer, { passive: true }));
    resetIdleTimer();

    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, resetIdleTimer));

      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
      }
    };
  }, [authStatus]);

  const handleLogin = async (credentials) => {
    const session = await loginWithPassword(credentials);
    setCurrentUser(session.user || null);
    setAuthStatus('authenticated');
  };

  const handleLogout = async () => {
    try {
      await logoutSession();
    } catch (error) {
      console.error('Unable to log out cleanly:', error);
    } finally {
      setCurrentUser(null);
      setAuthStatus('unauthenticated');
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<PublicHomepage />} />
          <Route path="/catalog" element={<CustomerCatalogPage />} />
          <Route path="/admin/*" element={<ProtectedAdmin authStatus={authStatus} user={currentUser} onLogin={handleLogin} onLogout={handleLogout} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}
